import { useMemo, useState } from 'react'
import { Button, DatePicker } from '../../components'
import { useAuditLog, type AuditEntityType, type AuditAction, type AuditEntry } from '../../lib/api/audit-log'
import { useAppState } from '../../app-state'
import { useTransaction } from '../../lib/api/transactions'

const ENTITY_LABELS: Record<AuditEntityType, string> = {
  transactions: 'Операції',
  licenses: 'Ліцензії',
  license_tariffs: 'Тарифи',
  license_payments: 'Виплати за ліцензіями',
  invoices: 'Інвойси',
  invoice_payments: 'Оплати інвойсів',
}

const ACTION_LABELS: Record<AuditAction, string> = {
  create: 'Створення',
  update: 'Зміна',
  delete: 'Видалення',
}

const ACTION_COLORS: Record<AuditAction, string> = {
  create: 'var(--success-fg)',
  update: 'var(--fg-2)',
  delete: 'var(--danger-fg)',
}

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 4,
  padding: '6px 8px',
  background: 'var(--bg-1)',
  color: 'var(--fg-1)',
  fontSize: 13,
}

export function AuditLogSection() {
  const [entityType, setEntityType] = useState<AuditEntityType | ''>('')
  const [action, setAction] = useState<AuditAction | ''>('')
  const [userMode, setUserMode] = useState<'any' | 'system'>('any')
  const [entityId, setEntityId] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [selected, setSelected] = useState<AuditEntry | null>(null)

  const filters = useMemo(
    () => ({
      entityType: entityType || null,
      action: action || null,
      userId: userMode === 'system' ? 'system' : null,
      entityId: entityId.trim() || null,
      dateFrom: dateFrom || null,
      dateTo: dateTo || null,
    }),
    [entityType, action, userMode, entityId, dateFrom, dateTo],
  )

  const query = useAuditLog(filters)
  const items = query.data?.pages.flatMap((p) => p.items) ?? []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Лог змін</h2>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 8,
          padding: 12,
          border: '1px solid var(--border)',
          borderRadius: 6,
        }}
      >
        <FilterField label="Об'єкт">
          <select
            style={inputStyle}
            value={entityType}
            onChange={(e) => setEntityType(e.target.value as AuditEntityType | '')}
          >
            <option value="">Всі</option>
            {Object.entries(ENTITY_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="Дія">
          <select
            style={inputStyle}
            value={action}
            onChange={(e) => setAction(e.target.value as AuditAction | '')}
          >
            <option value="">Всі</option>
            {Object.entries(ACTION_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </FilterField>
        <FilterField label="Автор">
          <select
            style={inputStyle}
            value={userMode}
            onChange={(e) => setUserMode(e.target.value as 'any' | 'system')}
          >
            <option value="any">Всі</option>
            <option value="system">Тільки система (B24/sync)</option>
          </select>
        </FilterField>
        <FilterField label="ID об'єкта">
          <input
            style={inputStyle}
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            placeholder="Точний id"
          />
        </FilterField>
        <FilterField label="Дата з">
          <DatePicker
            style={inputStyle}
            value={dateFrom}
            onChange={setDateFrom}
            max={dateTo || undefined}
            clearable
            placeholder="Будь-яка"
          />
        </FilterField>
        <FilterField label="Дата до">
          <DatePicker
            style={inputStyle}
            value={dateTo}
            onChange={setDateTo}
            min={dateFrom || undefined}
            clearable
            placeholder="Будь-яка"
          />
        </FilterField>
      </div>

      {query.isLoading ? (
        <div style={{ color: 'var(--fg-3)' }}>Завантаження…</div>
      ) : items.length === 0 ? (
        <div style={{ color: 'var(--fg-3)' }}>Немає записів</div>
      ) : (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 6,
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--bg-2)' }}>
                {['Час', 'Дія', 'Зміна суми', 'Об\'єкт', 'ID', 'Автор'].map((label) => (
                  <th
                    key={label}
                    style={{
                      textAlign: 'left',
                      padding: '8px 12px',
                      fontWeight: 500,
                      color: 'var(--fg-3)',
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr
                  key={row.id}
                  onClick={() => setSelected(row)}
                  style={{
                    borderTop: '1px solid var(--border)',
                    cursor: 'pointer',
                  }}
                >
                  <td
                    style={{
                      padding: '8px 12px',
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.createdAt.replace('T', ' ').slice(0, 19)}
                  </td>
                  <td
                    style={{
                      padding: '8px 12px',
                      color: ACTION_COLORS[row.action],
                      fontWeight: 500,
                    }}
                  >
                    {ACTION_LABELS[row.action]}
                  </td>
                  <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                    <AmountCell entry={row} />
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {ENTITY_LABELS[row.entityType] ?? row.entityType}
                  </td>
                  <td
                    style={{
                      padding: '8px 12px',
                      fontFamily: 'var(--font-mono, monospace)',
                      fontSize: 12,
                      color: 'var(--fg-3)',
                    }}
                  >
                    {row.entityId.slice(0, 8)}…
                  </td>
                  <td style={{ padding: '8px 12px' }}>
                    {row.userId
                      ? row.userEmail ?? row.userId.slice(0, 8)
                      : <span style={{ color: 'var(--fg-4)' }}>system</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {query.hasNextPage && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => query.fetchNextPage()}
          disabled={query.isFetchingNextPage}
        >
          {query.isFetchingNextPage ? 'Завантаження…' : 'Завантажити ще'}
        </Button>
      )}

      {selected && <AuditDetail entry={selected} onClose={() => setSelected(null)} />}
    </div>
  )
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: 11,
        color: 'var(--fg-4)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}
    >
      <span>{label}</span>
      {children}
    </label>
  )
}

function AuditDetail({ entry, onClose }: { entry: AuditEntry; onClose: () => void }) {
  const { setDrawerTx, setToast } = useAppState()
  // A deleted operation no longer exists, so it can't be reopened.
  const canOpen = entry.entityType === 'transactions' && entry.action !== 'delete'
  const txQuery = useTransaction(canOpen ? entry.entityId : null)

  const fullDiff = computeDiff(entry.before, entry.after)
  const amountRows = fullDiff.filter((d) => AMOUNT_KEYS.has(d.key))
  const otherRows = fullDiff.filter((d) => !AMOUNT_KEYS.has(d.key))
  const beforeRow = isObj(entry.before) ? entry.before : null
  const afterRow = isObj(entry.after) ? entry.after : null

  function openOperation() {
    const tx = txQuery.data
    if (!tx) {
      setToast('Не вдалося завантажити операцію')
      return
    }
    setDrawerTx({ mode: 'edit', transaction: tx })
    onClose()
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-1)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          width: 'min(800px, 90vw)',
          maxHeight: '80vh',
          overflow: 'auto',
          padding: 20,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 8,
            marginBottom: 16,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 16 }}>
            {ACTION_LABELS[entry.action]} · {ENTITY_LABELS[entry.entityType] ?? entry.entityType}
          </h3>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {canOpen && (
              <Button
                variant="primary"
                size="sm"
                icon="external-link"
                onClick={openOperation}
                disabled={txQuery.isLoading || txQuery.isError}
              >
                {txQuery.isLoading
                  ? 'Завантаження…'
                  : txQuery.isError
                    ? 'Операція недоступна'
                    : 'Відкрити операцію'}
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}>
              Закрити
            </Button>
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--fg-3)', marginBottom: 12 }}>
          {entry.createdAt} ·{' '}
          {entry.userId
            ? entry.userEmail ?? entry.userId
            : 'system (B24 / sync)'}{' '}
          · id: {entry.entityId}
        </div>

        {amountRows.length > 0 && (
          <AmountChangeBlock rows={amountRows} beforeRow={beforeRow} afterRow={afterRow} />
        )}

        {otherRows.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div
              style={{
                fontSize: 11,
                color: 'var(--fg-4)',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              {amountRows.length > 0 ? 'Інші зміни' : 'Поля, що змінились'}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--bg-2)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500 }}>Поле</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500 }}>Було</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 500 }}>Стало</th>
                </tr>
              </thead>
              <tbody>
                {otherRows.map((d) => (
                  <tr key={d.key} style={{ borderTop: '1px solid var(--border)' }}>
                    <td
                      style={{
                        padding: '6px 8px',
                        fontFamily: 'var(--font-mono, monospace)',
                      }}
                    >
                      {d.key}
                    </td>
                    <td
                      style={{
                        padding: '6px 8px',
                        color: 'var(--danger-fg)',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {fmt(d.before)}
                    </td>
                    <td
                      style={{
                        padding: '6px 8px',
                        color: 'var(--success-fg)',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {fmt(d.after)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        <details style={{ marginTop: 16 }}>
          <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--fg-3)' }}>
            Повний снімок
          </summary>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
            <Snapshot title="До" value={entry.before} />
            <Snapshot title="Після" value={entry.after} />
          </div>
        </details>
      </div>
    </div>
  )
}

function Snapshot({ title, value }: { title: string; value: unknown }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--fg-4)',
          marginBottom: 4,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {title}
      </div>
      <pre
        style={{
          background: 'var(--bg-2)',
          border: '1px solid var(--border)',
          borderRadius: 4,
          padding: 8,
          fontSize: 11,
          overflow: 'auto',
          maxHeight: 240,
          margin: 0,
        }}
      >
        {value == null ? '—' : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  )
}

type DiffRow = { key: string; before: unknown; after: unknown }

function computeDiff(before: unknown, after: unknown): DiffRow[] {
  if (!isObj(before) && !isObj(after)) return []
  const b = (isObj(before) ? before : {}) as Record<string, unknown>
  const a = (isObj(after) ? after : {}) as Record<string, unknown>
  const keys = new Set([...Object.keys(b), ...Object.keys(a)])
  const out: DiffRow[] = []
  for (const k of keys) {
    if (k === 'updated_at') continue
    if (!equal(b[k], a[k])) out.push({ key: k, before: b[k], after: a[k] })
  }
  return out.sort((x, y) => x.key.localeCompare(y.key))
}

function isObj(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

function equal(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (a == null && b == null) return true
  if (typeof a !== typeof b) return false
  if (typeof a === 'object') return JSON.stringify(a) === JSON.stringify(b)
  return false
}

function fmt(v: unknown): string {
  if (v == null) return '—'
  if (typeof v === 'string') return v
  if (typeof v === 'number' || typeof v === 'boolean') return String(v)
  return JSON.stringify(v)
}

// Amount-bearing columns (raw snapshot uses the DB snake_case names). Amounts are
// the main source of bookkeeping errors, so we surface their changes first — both
// inline in the list and as a highlighted block atop the detail modal. Order here
// drives the display order in the block.
const AMOUNT_FIELDS: Record<string, string> = {
  amount: 'Сума',
  currency: 'Валюта',
  amount_base: 'Сума (база, UAH)',
  to_amount: 'Сума зарахування',
  to_currency: 'Валюта зарахування',
  fx_rate: 'Курс',
}
const AMOUNT_ORDER = Object.keys(AMOUNT_FIELDS)
const AMOUNT_KEYS = new Set(AMOUNT_ORDER)

const numFmt = new Intl.NumberFormat('uk-UA', { maximumFractionDigits: 2 })

function formatNumber(v: unknown): string {
  return typeof v === 'number' ? numFmt.format(v) : fmt(v)
}

function formatMoney(value: unknown, currency: unknown): string {
  if (value == null) return '—'
  const n = formatNumber(value)
  return typeof currency === 'string' && currency ? `${n} ${currency}` : n
}

function formatAmountField(key: string, value: unknown, row: Record<string, unknown> | null): string {
  if (value == null) return '—'
  switch (key) {
    case 'amount':
      return formatMoney(value, row?.currency)
    case 'amount_base':
      return formatMoney(value, 'UAH')
    case 'to_amount':
      return formatMoney(value, row?.to_currency)
    case 'fx_rate':
      return formatNumber(value)
    default:
      return fmt(value)
  }
}

// Signed delta for numeric amount fields, e.g. "+200" / "−50". Null for non-numeric
// changes (currency code) or when nothing actually moved.
function amountDelta(before: unknown, after: unknown): string | null {
  if (typeof before !== 'number' || typeof after !== 'number') return null
  const d = after - before
  if (d === 0) return null
  return `${d > 0 ? '+' : '−'}${numFmt.format(Math.abs(d))}`
}

// The headline amount shown inline in the log list: the before→after change for an
// edit, or the plain amount for a create/delete. Null when the row carries no amount
// or an edit left the amount (and currency) untouched.
function AmountCell({ entry }: { entry: AuditEntry }) {
  const b = isObj(entry.before) ? entry.before : null
  const a = isObj(entry.after) ? entry.after : null
  const bAmt = b?.amount
  const aAmt = a?.amount
  if (typeof bAmt !== 'number' && typeof aAmt !== 'number') {
    return <span style={{ color: 'var(--fg-4)' }}>—</span>
  }

  if (entry.action === 'create') {
    return <span style={{ color: 'var(--success-fg)' }}>{formatMoney(aAmt, a?.currency)}</span>
  }
  if (entry.action === 'delete') {
    return (
      <span style={{ color: 'var(--fg-3)', textDecoration: 'line-through' }}>
        {formatMoney(bAmt, b?.currency)}
      </span>
    )
  }

  const amtChanged = bAmt !== aAmt
  const curChanged = (b?.currency ?? null) !== (a?.currency ?? null)
  if (!amtChanged && !curChanged) return <span style={{ color: 'var(--fg-4)' }}>—</span>

  const delta = amountDelta(bAmt, aAmt)
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6, fontVariantNumeric: 'tabular-nums' }}>
      <span style={{ color: 'var(--danger-fg)', textDecoration: 'line-through' }}>
        {formatMoney(bAmt, b?.currency)}
      </span>
      <span style={{ color: 'var(--fg-4)' }}>→</span>
      <span style={{ color: 'var(--success-fg)', fontWeight: 600 }}>
        {formatMoney(aAmt, a?.currency)}
      </span>
      {delta && (
        <span style={{ fontSize: 11, color: delta.startsWith('+') ? 'var(--success-fg)' : 'var(--danger-fg)' }}>
          ({delta})
        </span>
      )}
    </span>
  )
}

function AmountChangeBlock({
  rows,
  beforeRow,
  afterRow,
}: {
  rows: DiffRow[]
  beforeRow: Record<string, unknown> | null
  afterRow: Record<string, unknown> | null
}) {
  const ordered = [...rows].sort((x, y) => AMOUNT_ORDER.indexOf(x.key) - AMOUNT_ORDER.indexOf(y.key))
  return (
    <div
      style={{
        border: '1px solid var(--warning)',
        background: 'var(--warning-bg)',
        borderRadius: 6,
        padding: 12,
        marginBottom: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: 'var(--warning-fg)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 600,
        }}
      >
        Зміна сум
      </div>
      {ordered.map((d) => {
        const delta = amountDelta(d.before, d.after)
        return (
          <div key={d.key} style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ minWidth: 140, fontSize: 12, color: 'var(--fg-3)' }}>
              {AMOUNT_FIELDS[d.key] ?? d.key}
            </span>
            <span style={{ color: 'var(--danger-fg)', textDecoration: 'line-through' }}>
              {formatAmountField(d.key, d.before, beforeRow)}
            </span>
            <span style={{ color: 'var(--fg-4)' }}>→</span>
            <span style={{ color: 'var(--success-fg)', fontWeight: 700, fontSize: 15 }}>
              {formatAmountField(d.key, d.after, afterRow)}
            </span>
            {delta && (
              <span style={{ fontSize: 12, color: delta.startsWith('+') ? 'var(--success-fg)' : 'var(--danger-fg)' }}>
                ({delta})
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}
